import { useEffect } from "react";

/**
 * Lo fijo del teléfono (header, nav del torneo, barras de abajo, botón de
 * ayuda) pegado a lo que SÍ se ve, aunque iOS 26 se desajuste.
 *
 * Reporte de Eduardo (2026-10-06, iPhone iOS 26.7.1, app instalada sin barra
 * de Safari, paso Draw): al bajar, la barra «Operación del torneo» quedó ~300
 * puntos arriba con contenido debajo, el header desapareció y el botón de
 * ayuda subió igual. Es el bug de WebKit 297779 / FB19889436: tras abrir y
 * cerrar el teclado (en cualquier momento de la sesión; en la app instalada
 * dura hasta cerrarla) iOS 26 se queda con el alto «con teclado» del visual
 * viewport. Al bajar, la parte visible se va al fondo del layout viewport y
 * `visualViewport.offsetTop` queda en ~300 (el alto del teclado): todo lo
 * `position: fixed` (y sticky) se dibuja esos 300 puntos más arriba. Al subir
 * offsetTop vuelve a 0 y todo regresa a su lugar.
 *
 * Arreglo: medir `visualViewport.offsetTop` y bajar TODO el chrome fijo esa
 * misma cantidad (variable `--torneos-viewport-shift` + atributo
 * `data-viewport-shift` en <html>; styles.css ≤760px usa `translate`). En lo
 * normal offsetTop es 0, el atributo no existe y nada cambia. No corrige:
 * - con un campo de texto enfocado (teclado de verdad abierto: se deja el
 *   comportamiento nativo de iOS);
 * - con zoom de dos dedos (scale ≠ 1: ahí offsetTop es legítimo);
 * - en pantallas de más de 760px (computadora).
 */

export const VIEWPORT_SHIFT_VAR = "--torneos-viewport-shift";
export const VIEWPORT_SHIFT_ATTR = "data-viewport-shift";

const MOBILE_QUERY = "(max-width: 760px)";

type ViewportBox = Pick<VisualViewport, "offsetTop" | "scale">;

const NON_TEXT_INPUTS = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

/** ¿El elemento enfocado abre el teclado? */
export function opensKeyboard(element: Element | null | undefined): boolean {
  if (!element) return false;
  if (element instanceof HTMLTextAreaElement) return true;
  if (element instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(element.type);
  return element instanceof HTMLElement && element.isContentEditable === true;
}

/** Píxeles que hay que bajar lo fijo para alinearlo con lo visible. */
export function visualViewportChromeShift(
  viewport: ViewportBox | null | undefined,
  keyboardFocused: boolean,
): number {
  if (!viewport || keyboardFocused) return 0;
  const scale = Number.isFinite(viewport.scale) ? viewport.scale : 1;
  if (Math.abs(scale - 1) > 0.01) return 0;
  // Menos de 1px es redondeo; negativo es el rebote del scroll arriba.
  return viewport.offsetTop > 1 ? Math.round(viewport.offsetTop) : 0;
}

export default function useVisualViewportChromeShift(): void {
  useEffect(() => {
    const viewport = typeof window === "undefined" ? undefined : window.visualViewport;
    if (!viewport || typeof window.matchMedia !== "function") return;
    const root = document.documentElement;
    const mobile = window.matchMedia(MOBILE_QUERY);

    let frame = 0;
    let applied = 0;
    const update = () => {
      frame = 0;
      const next = mobile.matches ? visualViewportChromeShift(viewport, opensKeyboard(document.activeElement)) : 0;
      if (next === applied) return;
      applied = next;
      if (next > 0) {
        root.style.setProperty(VIEWPORT_SHIFT_VAR, `${next}px`);
        root.setAttribute(VIEWPORT_SHIFT_ATTR, "");
      } else {
        root.style.removeProperty(VIEWPORT_SHIFT_VAR);
        root.removeAttribute(VIEWPORT_SHIFT_ATTR);
      }
    };
    const schedule = () => {
      if (frame === 0) frame = window.requestAnimationFrame(update);
    };

    viewport.addEventListener("resize", schedule);
    viewport.addEventListener("scroll", schedule);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    document.addEventListener("focusin", schedule);
    document.addEventListener("focusout", schedule);
    mobile.addEventListener?.("change", schedule);
    update();

    return () => {
      viewport.removeEventListener("resize", schedule);
      viewport.removeEventListener("scroll", schedule);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("focusin", schedule);
      document.removeEventListener("focusout", schedule);
      mobile.removeEventListener?.("change", schedule);
      if (frame !== 0) window.cancelAnimationFrame(frame);
      root.style.removeProperty(VIEWPORT_SHIFT_VAR);
      root.removeAttribute(VIEWPORT_SHIFT_ATTR);
    };
  }, []);
}
